import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Copy, Info, ListTree, Mail, Search } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import SeoHead from "../components/SeoHead";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { useLanguage } from "../context/LanguageContext";

const Block = ({ block }) => {
  if (block.type === "h") {
    return <h3 className="mt-7 font-heading text-lg font-bold text-slate-900">{block.text}</h3>;
  }
  if (block.type === "p") {
    return <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">{block.text}</p>;
  }
  if (block.type === "list") {
    return (
      <ul className="mt-4 space-y-2.5">
        {block.items.map((item, i) => (
          <li key={i} className="flex gap-3 text-sm leading-relaxed text-slate-600 sm:text-base">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
            {item}
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "steps") {
    return (
      <ol className="mt-4 space-y-3">
        {block.items.map((item, i) => (
          <li key={i} className="flex gap-4 text-sm leading-relaxed text-slate-600 sm:text-base">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-600 font-mono text-xs font-bold text-white">
              {i + 1}
            </span>
            <span className="pt-0.5">{item}</span>
          </li>
        ))}
      </ol>
    );
  }
  if (block.type === "note") {
    return (
      <div className="mt-5 flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        {block.text}
      </div>
    );
  }
  return null;
};

const HelpPage = () => {
  const { t, lang } = useLanguage();
  const [query, setQuery] = useState("");
  const sections = t.help.sections;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return sections.filter((s) =>
      JSON.stringify(s).toLowerCase().includes(q)
    );
  }, [query, sections]);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText("support@lingomind.app");
      toast.success(t.helpPage.copied);
    } catch {
      toast.info("support@lingomind.app");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] text-slate-900" data-testid="help-page">
      <SeoHead page="help" />
      <Header />
      <main className="pt-16">
        <section className="relative overflow-hidden border-b border-sky-100 bg-gradient-to-br from-sky-50 via-white to-amber-50/60 py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-8">
            <Reveal>
              <Link
                to={`/${lang}`}
                data-testid="help-back-home"
                className="inline-flex items-center gap-2 text-sm font-semibold text-sky-700 transition-colors hover:text-amber-600"
              >
                <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
                {t.helpPage.back}
              </Link>
              <p className="mt-6 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-amber-600">
                {t.helpPage.overline}
              </p>
              <h1 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
                {t.helpPage.title}
              </h1>
              <p className="mt-4 max-w-2xl text-base text-slate-600 sm:text-lg">{t.helpPage.subtitle}</p>
              <div className="mt-8 flex max-w-md items-center gap-2 rounded-2xl border border-sky-200 bg-white px-4 py-3 shadow-sm focus-within:border-sky-500">
                <Search className="h-4 w-4 shrink-0 text-sky-500" />
                <input
                  data-testid="help-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t.helpPage.search}
                  className="w-full bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400"
                />
              </div>
            </Reveal>
          </div>
        </section>

        <section className="mx-auto max-w-7xl gap-10 px-4 py-14 sm:px-8 lg:grid lg:grid-cols-[260px_1fr]">
          <aside className="mb-10 lg:mb-0">
            <div className="rounded-3xl border border-sky-100 bg-white p-6 lg:sticky lg:top-24">
              <h2 className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                <ListTree className="h-4 w-4 text-sky-600" />
                {t.helpPage.toc}
              </h2>
              <nav className="mt-4 space-y-1">
                {filtered.map((s) => (
                  <a
                    key={s.id}
                    data-testid={`help-toc-${s.id}`}
                    href={`#${s.id}`}
                    className="flex items-baseline gap-3 rounded-xl px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-sky-50 hover:text-sky-700"
                  >
                    <span className="font-mono text-xs font-bold text-amber-600">{s.num}</span>
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          <div className="min-w-0">
            {filtered.length === 0 && (
              <p className="rounded-2xl border border-sky-100 bg-white p-8 text-center text-sm text-slate-500" data-testid="help-no-results">
                {t.helpPage.noResults}
              </p>
            )}
            <div className="space-y-6">
              {filtered.map((s, i) => (
                <Reveal key={s.id} delay={Math.min(i * 0.04, 0.3)}>
                  <article
                    id={s.id}
                    data-testid={`help-section-${s.id}`}
                    className="scroll-mt-24 rounded-3xl border border-sky-100 bg-white p-7 shadow-sm sm:p-10"
                  >
                    <div className="flex items-baseline gap-4 border-b border-sky-50 pb-5">
                      <span className="font-heading text-3xl font-extrabold text-sky-100 sm:text-4xl">
                        {s.num}
                      </span>
                      <h2 className="font-heading text-xl font-bold text-slate-900 sm:text-2xl">
                        {s.title}
                      </h2>
                    </div>
                    <div>
                      {s.blocks.map((b, bi) => (
                        <Block key={bi} block={b} />
                      ))}
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <div className="mt-10 overflow-hidden rounded-3xl bg-gradient-to-br from-sky-600 to-sky-800 p-8 text-white sm:p-10" data-testid="help-support-card">
                <h2 className="font-heading text-2xl font-bold">{t.helpPage.stillNeed}</h2>
                <p className="mt-2 max-w-lg text-sm text-sky-100 sm:text-base">{t.helpPage.stillNeedDesc}</p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <a
                    data-testid="help-email-button"
                    href="mailto:support@lingomind.app"
                    className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-amber-900/30 transition-all hover:-translate-y-0.5 hover:bg-amber-400"
                  >
                    <Mail className="h-4 w-4" />
                    {t.helpPage.emailBtn}
                  </a>
                  <button
                    data-testid="help-copy-email-button"
                    onClick={copyEmail}
                    className="inline-flex items-center gap-2 rounded-full border border-white/30 px-5 py-3 text-sm font-semibold text-white transition-colors hover:border-amber-300 hover:text-amber-300"
                  >
                    <Copy className="h-4 w-4" />
                    support@lingomind.app
                  </button>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HelpPage;
